import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Admin Revenue tab: the trials-ending list and the revenue summary.
 *
 * Wire format only. Every figure counts REAL customers (internal, test, admin
 * and soft-deleted accounts are excluded server-side). Dates are ISO strings.
 */

// ── Trials ending ─────────────────────────────────────────────────────

/**
 * Look-ahead choices the admin UI offers. The endpoint accepts any whole number
 * up to the maximum below, so a script can ask for 10 days; the UI sticks to
 * these three.
 */
export const TRIALS_WITHIN_DAYS_OPTIONS = [3, 7, 14] as const;

/**
 * Default and ceiling of `withinDays`. These mirror ADMIN_TRIALS_DEFAULT_WITHIN_DAYS
 * and ADMIN_TRIALS_MAX_WITHIN_DAYS in `apps/server/src/config/domains/observability.config.ts`
 * (the libs cannot import server config); a contract test pins the two together.
 */
export const trialsQuerySchema = z.object({
  withinDays: z.coerce.number().int().min(1).max(30).default(7),
});

export const checkoutAttemptsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export class CheckoutAttemptsQueryDto extends createZodDto(checkoutAttemptsQuerySchema) {}

export const checkoutAttemptTimelineSchema = z.object({
  attemptId: z.string().optional(),
  status: z.enum([
    "pending",
    "complete",
    "create_failed",
    "provisioning_failure",
    "returned_no_payment_signal",
    "unobserved_checkout",
  ]),
  gapMs: z.number(),
  events: z.array(z.object({ event: z.string(), at: z.string(), props: z.record(z.unknown()) })),
});
export type CheckoutAttemptTimeline = z.infer<typeof checkoutAttemptTimelineSchema>;

export const trialRowSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  /** End of the trial (`Subscription.lsCurrentPeriodEnd`), ISO. */
  trialEndsAt: z.string(),
  /** Whole days from now until the trial ends, rounded up; 0 means "ends within a day". */
  daysLeft: z.number().int(),
  signedUpAt: z.string(),
  hasResume: z.boolean(),
  hasJobProfile: z.boolean(),
  /** Latest `JobProfile.lastViewedAt` across the user's profiles; null if never opened a job. */
  lastOpenedJobAt: z.string().nullable(),
  /** Applications with a tailored résumé or tailored summary. */
  tailoredCount: z.number().int(),
  /** Applications the user actually submitted (`submittedVia` is set). */
  submittedApplications: z.number().int(),
  paywallViews: z.number().int(),
  checkoutStarts: z.number().int(),
  trialReminderSentAt: z.string().nullable(),
  trialEndedEmailSentAt: z.string().nullable(),
  postExpiryUpsellSentAt: z.string().nullable(),
});

export const trialsResponseSchema = z.object({
  generatedAt: z.string(),
  withinDays: z.number().int(),
  rows: z.array(trialRowSchema),
  /** True when more trialists match than the server returns in one response. */
  truncated: z.boolean(),
});

// ── Revenue summary ───────────────────────────────────────────────────

export const revenueCurrencyTotalSchema = z.object({
  currency: z.string(),
  count: z.number().int(),
  /** Sum of original order amounts in the currency's minor unit (cents). */
  amountMinor: z.number().int(),
});

/** A count over three windows. `events` counts rows; `users` counts distinct people. */
export const revenueEventWindowsSchema = z.object({
  last24h: z.object({ events: z.number().int(), users: z.number().int() }),
  last7d: z.object({ events: z.number().int(), users: z.number().int() }),
  allTime: z.object({ events: z.number().int(), users: z.number().int() }),
});

/**
 * Stable keys for the caveats the payment rows cannot answer. The server sends
 * keys, not prose, so the client can translate each one.
 */
export const REVENUE_CAVEAT_KEYS = [
  "settlement-unavailable",
  "refunds-fees-unavailable",
  "mrr-unavailable",
  "payment-may-predate-subscription",
] as const;

export const revenueCaveatKeySchema = z.enum(REVENUE_CAVEAT_KEYS);

export const revenueSummaryResponseSchema = z.object({
  generatedAt: z.string(),
  payments: z.object({
    /** Succeeded payments of more than zero by real users, all time. */
    succeededCount: z.number().int(),
    byCurrency: z.array(revenueCurrencyTotalSchema),
    /** Real users who count as paid (succeeded payment > 0, or an ACTIVE Lemon Squeezy plan). */
    paidCustomers: z.number().int(),
  }),
  checkoutStarted: revenueEventWindowsSchema,
  paywallViewed: revenueEventWindowsSchema,
  trials: z.object({
    /** TRIALING subscriptions whose end date is still ahead. */
    active: z.number().int(),
    endingWithin3d: z.number().int(),
    endingWithin7d: z.number().int(),
    /** Trials whose 14-day window ended inside the conversion window. */
    expiredInWindow: z.number().int(),
    /** Of those, users who now count as paid. */
    convertedToPaid: z.number().int(),
    /** convertedToPaid / expiredInWindow; null when no trial expired in the window. */
    conversionRate: z.number().nullable(),
    conversionWindowDays: z.number().int(),
  }),
  /** Keys of the caveats from the revenue baseline (what the payment rows cannot tell us). */
  unknowns: z.array(revenueCaveatKeySchema),
});

export type TrialsQuery = z.infer<typeof trialsQuerySchema>;
export type TrialRow = z.infer<typeof trialRowSchema>;
export type TrialsResponse = z.infer<typeof trialsResponseSchema>;
export type RevenueCurrencyTotal = z.infer<typeof revenueCurrencyTotalSchema>;
export type RevenueCaveatKey = z.infer<typeof revenueCaveatKeySchema>;
export type RevenueEventWindows = z.infer<typeof revenueEventWindowsSchema>;
export type RevenueSummaryResponse = z.infer<typeof revenueSummaryResponseSchema>;

export class TrialsQueryDto extends createZodDto(trialsQuerySchema) {}
